import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BarcodeScanner, DetectedBarcode, ScanOptions } from 'react-barcode-scanner';
import 'react-barcode-scanner/polyfill';
import { toast } from 'react-toastify';
import { Button, Input, Modal, ModalBody, TabContent, TabPane } from 'reactstrap';

import { EventPeriod } from 'types';

type ScanBarCodeModalProps = {
  eventPeriods: EventPeriod[];
  show: boolean;
  onCloseClick: () => void;
  scanningPhase: string;
  setScanningPhase: (newData: 'Scanning' | 'Processing' | 'Done') => void;
  getParticipantByStudentId: (newdata: string) => Promise<void>;
  setScannedPeriodId: (newdata: string) => void;
};

const scanOptions: ScanOptions = {
  delay: 1000,
  formats: ['code_128', 'qr_code'],
};

const ScanBarCodeModal = ({
  eventPeriods,
  show,
  onCloseClick,
  scanningPhase,
  getParticipantByStudentId,
  setScannedPeriodId,
}: ScanBarCodeModalProps) => {
  const [cameraFacingMode, setCameraFacingMode] = useState<'environment' | 'user'>('environment');

  const [scanningData, setScanningData] = useState('');
  const isScanningRef = useRef(true);
  const debounceSetScanningData = debounce((value: string) => setScanningData(value), 500);
  const getParticipant = useCallback(
    async (studentId: string) => {
      try {
        await getParticipantByStudentId(studentId);
      } catch (err) {
        console.error('Error fetching participant:', err);
        toast.error('Failed to fetch participant.');
      } finally {
        isScanningRef.current = true;
        setScanningData('');
      }
    },
    [getParticipantByStudentId]
  );

  useEffect(() => {
    if (scanningData && isScanningRef.current === false) getParticipant(scanningData);
  }, [scanningData, getParticipant]);

  return (
    <Modal isOpen={show} toggle={onCloseClick} centered={true} size='sm' backdrop={true}>
      <div className='modal-content'>
        <ModalBody className='tw-p-2 tw-flex tw-flex-col tw-items-center tw-gap-4'>
          <TabContent>
            <TabPane>
              <div className='tw-flex tw-flex-col tw-items-center tw-gap-4'>
                <div className='tw-font-bold tw-text-[1.5rem]'>Attendance Registration</div>
                <Input
                  id='type'
                  name='type'
                  type='select'
                  className='tw-w-[8rem] tw-h-[2.5rem] tw-flex tw-items-center tw-relative'
                  placeholder='Enter period type...'
                  onChange={(e) => {
                    const value = e.target.value;
                    setScannedPeriodId(value);
                  }}
                >
                  {eventPeriods.map((period) => (
                    <option key={period._id} value={period._id}>
                      {period.title}
                    </option>
                  ))}
                </Input>
                <Input
                  id='camera-mode'
                  name='camera-mode'
                  type='select'
                  className='tw-w-[8rem] tw-h-[2.5rem] tw-flex tw-items-center tw-relative'
                  value={cameraFacingMode}
                  onChange={(e) => setCameraFacingMode(e.target.value as 'environment' | 'user')}
                >
                  <option value='environment'>Back Camera</option>
                  <option value='user'>Front Camera</option>
                </Input>

                <div className='tw-relative tw-w-[15rem] tw-flex tw-flex-col'>
                  <BarcodeScanner
                    trackConstraints={{
                      facingMode: {
                        ideal: cameraFacingMode,
                      },
                    }}
                    options={scanOptions}
                    onCapture={(barcodes: DetectedBarcode[]) => {
                      if (
                        !barcodes.length ||
                        isScanningRef.current === false ||
                        scanningData !== ''
                      )
                        return;
                      if (barcodes && barcodes.length > 0) {
                        isScanningRef.current = false;
                        const studentId = barcodes[0].rawValue;
                        debounceSetScanningData(studentId);
                      }
                    }}
                    className='tw-relative tw-w-[15rem] tw-aspect-square tw-border-8 tw-border-[#3D4863] tw-border-solid tw-rounded-lg'
                  />
                </div>
                <div
                  className={`${
                    scanningPhase === 'Done'
                      ? 'tw-text-[#0F9D58]'
                      : scanningPhase === 'Done'
                      ? 'tw-text-[#F1B44C]'
                      : ''
                  } tw-font-bold`}
                >
                  {scanningPhase}
                </div>
              </div>
            </TabPane>
          </TabContent>
          <Button type='button' color='primary' onClick={onCloseClick}>
            Close
          </Button>
        </ModalBody>
      </div>
    </Modal>
  );
};

export default ScanBarCodeModal;
